import { useEffect, useMemo, useState } from 'react';
import { FieldValues, useWatch } from 'react-hook-form';
import useCreateRecordDialog from 'hooks/useCreateRecordDialog';
import {
  useCreateOrgSupplierMutation,
  useGetOrgSuppliersServerSideQuery,
  useGetPublicSuppliersServerSideQuery,
  useUpdateOrgSupplierCredentialMutation,
} from 'store/slices/apiV1/suppliers';
import {
  formConstants,
  simplifiedCreateValidationSchema,
} from 'views/suppliers/CreateEditOrgSupplierDialog/constants';
import {
  OrgSupplier,
  PublicSupplier,
  CreateOrgSupplier,
  LoginRequired,
  OrgSupplierConnection,
  OrgSupplierConnectionLabels,
  PutOrgSupplierCredential,
} from 'types/suppliers';
import { ALL_RECORD_TYPE_IDS } from 'constants/recordTypes';
import { ALL_APP_IDS } from 'constants/appConstants';
import { appPermissionAccessLevels } from 'types/apps';
import { useDebouncedValue } from 'views/sourcing/SupplierSearch/utils';
import { useSearchBrandsQuery } from 'store/slices/apiV1/brandfetch';
import {
  AlertTitle,
  Alert,
  Avatar,
  CircularProgress,
  Grid,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { actionButtonSpacing, gridSpacing } from 'constants/themeConstants';
import { BrandfetchSearchResult } from 'types';
import {
  IconArrowLeft,
  IconCheck,
  IconCircleCheck,
  IconExternalLink,
  IconPencil,
  IconRoute2,
  IconSearch,
  IconTopologyStar,
  IconWorldSearch,
} from '@tabler/icons-react';
import SegmentButton from 'ui-component/Segment/SegmentButton';
import { stripUrlToDomainWithTld } from 'utils/urls';
import { useTheme } from '@mui/material/styles';
import InlineGoToButton from 'ui-component/shared/InlineGoToButton';
import { FieldFilter, QueryFilterOperators } from 'types/api';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import _ from 'lodash';
import {
  HookFormInput,
  HookFormToggleCardButton,
} from 'ui-component/HookFormComponents';
import { useSelector } from 'store';
import { useGetOrgQuery } from 'store/slices/apiV1/org';

type CreateOrgSupplierV2DialogProps = {
  dialogOpen: boolean;
  onClose: () => void;
  publicSupplier?: PublicSupplier | null;
  setSelectedSupplier?: (supplier: OrgSupplier) => void;
};

enum CreateOrgSupplierV2DialogStep {
  SEARCH = 'search',
  EDIT_SUPPLIER = 'edit_supplier',
}

const CreateOrgSupplierV2Dialog = ({
  dialogOpen,
  onClose,
  publicSupplier: initialPublicSupplier,
  setSelectedSupplier,
}: CreateOrgSupplierV2DialogProps) => {
  const theme = useTheme();
  const [searchString, setSearchString] = useState<string>('');
  const debouncedSearchString = useDebouncedValue(searchString, 500);
  const [selectedBrand, setSelectedBrand] =
    useState<BrandfetchSearchResult | null>(null);
  const [selectedPublicSupplier, setSelectedPublicSupplier] =
    useState<PublicSupplier | null>(initialPublicSupplier || null);
  const [step, setStep] = useState<CreateOrgSupplierV2DialogStep>(
    initialPublicSupplier
      ? CreateOrgSupplierV2DialogStep.EDIT_SUPPLIER
      : CreateOrgSupplierV2DialogStep.SEARCH
  );
  const [proxyTargetIp, setProxyTargetIp] = useState<string>('');

  const { activeOrgId } = useSelector((state) => state.org);
  const { data: org } = useGetOrgQuery(activeOrgId ?? skipToken);

  const [
    updateOrgSupplierCredential,
    { isLoading: isUpdatingOrgSupplierCredential },
  ] = useUpdateOrgSupplierCredentialMutation();

  const formattedSearchString = useMemo(() => {
    if (debouncedSearchString.trim().startsWith('http')) {
      return stripUrlToDomainWithTld(debouncedSearchString.trim()) ?? '';
    }
    return debouncedSearchString.trim() ?? '';
  }, [debouncedSearchString]);

  const {
    data: brandfetchSearchResults,
    isFetching: isBrandfetchSearchLoading,
  } = useSearchBrandsQuery(
    formattedSearchString.length > 0 ? formattedSearchString : skipToken
  );

  const searchStringIsLink = useMemo(
    () => searchString.trim().startsWith('http'),
    [searchString]
  );

  useEffect(() => {
    if (dialogOpen) {
      fetch('https://api.ipify.org?format=json')
        .then((response) => response.json())
        .then((data: { ip: string }) => setProxyTargetIp(data.ip))
        .catch(() => {
          setProxyTargetIp('');
        });
    }
  }, [dialogOpen]);

  const defaultValues = useMemo(
    () => ({
      [formConstants.connectionType.id]:
        !org || org?.allowCofactrManagedPurchasing
          ? OrgSupplierConnection.SUPPLIER_NETWORK
          : OrgSupplierConnection.DIRECT,
      [formConstants.name.id]: initialPublicSupplier?.name || '',
      [formConstants.website.id]: initialPublicSupplier?.website || '',
      [formConstants.orderEmail.id]: '',
      [formConstants.quotingEmail.id]: '',
      [formConstants.paymentEmail.id]: '',
      [formConstants.username.id]: '',
      [formConstants.password.id]: '',
    }),
    [
      org?.allowCofactrManagedPurchasing,
      initialPublicSupplier?.name,
      initialPublicSupplier?.website,
    ]
  );

  const { data: { data: duplicateSuppliers = [] } = { data: [] } } =
    useGetOrgSuppliersServerSideQuery(
      selectedPublicSupplier
        ? {
            schema: ['id', 'name'],
            filters: [
              {
                field: 'objectId',
                operator: 'equals',
                value: selectedPublicSupplier?.objectId,
              },
            ],
            pageSize: 1,
          }
        : skipToken
    );

  const { data: publicSuppliers, isFetching: isFetchingPublicSuppliers } =
    useGetPublicSuppliersServerSideQuery(
      (brandfetchSearchResults || []).length > 0
        ? {
            schema: [
              'id',
              'name',
              'website',
              'loginRequired',
              'altNames',
              'objectId',
            ],
            filters: [
              ..._.chain(brandfetchSearchResults || [])
                .flatMap((result) => [
                  {
                    field: 'website' as const,
                    operator: QueryFilterOperators.contains,
                    value: result.domain,
                  },
                  'or' as const,
                ])
                .dropRight()
                .value(),
              'or' as const,
              ..._.chain(brandfetchSearchResults || [])
                .flatMap((result) => [
                  {
                    field: 'name' as const,
                    operator: QueryFilterOperators.contains,
                    value: result.name,
                  },
                  'or' as const,
                ])
                .dropRight()
                .value(),
              'or' as const,
              ..._.chain(brandfetchSearchResults || [])
                .flatMap((result) => [
                  {
                    field: 'altNames' as const,
                    operator: QueryFilterOperators.contains,
                    value: result.name,
                  },
                  'or' as const,
                ])
                .dropRight()
                .value(),
            ] as [FieldFilter<PublicSupplier>],
            pageSize: (brandfetchSearchResults || []).length,
          }
        : skipToken
    );

  const { methods, BaseCreateRecordDialog, dialogProps } =
    useCreateRecordDialog<FieldValues, CreateOrgSupplier>({
      formConstants,
      validationSchema: simplifiedCreateValidationSchema,
      newRecordDefaults: defaultValues,
      useCreateMutation: useCreateOrgSupplierMutation,
      recordName: 'Supplier',
      onSave: (record) => {
        if (record && setSelectedSupplier) {
          setSelectedSupplier(record as OrgSupplier);
        }
        if (record && watchUsername && watchPassword) {
          updateOrgSupplierCredential({
            orgSupplierId: (record as OrgSupplier).id,
            payload: {
              username: watchUsername,
              password: watchPassword,
              proxyTargetIp,
            } as PutOrgSupplierCredential,
          });
        }
        setSelectedBrand(null);
        setSearchString('');
        setSelectedPublicSupplier(null);
        setStep(CreateOrgSupplierV2DialogStep.SEARCH);
      },
      recordType: ALL_RECORD_TYPE_IDS.ORG_SUPPLIER,
      permissionScope: {
        app: ALL_APP_IDS.SUPPLIERS,
        accessLevel: appPermissionAccessLevels.edit,
      },
      allowAttachments: false,
      preSubmit: (data) => ({
        ..._.omit(data, [formConstants.username.id, formConstants.password.id]),
        publicSupplier: selectedPublicSupplier
          ? selectedPublicSupplier.id
          : null,
        alsoCreatePublicSupplier: false,
        [formConstants.orderEmail.id]: data[formConstants.quotingEmail.id],
        [formConstants.paymentEmail.id]: data[formConstants.quotingEmail.id],
        ...(selectedBrand
          ? {
              [formConstants.name.id]: selectedBrand.name,
              [formConstants.website.id]: selectedBrand.domain,
            }
          : {}),
      }),
      isSubmitting: isUpdatingOrgSupplierCredential,
    });

  const watchUsername = useWatch({
    control: methods.control,
    name: formConstants.username.id,
  });
  const watchPassword = useWatch({
    control: methods.control,
    name: formConstants.password.id,
  });

  useEffect(() => {
    if (dialogOpen) {
      methods.reset(defaultValues);
      setSelectedBrand(null);
      setSearchString('');
      setSelectedPublicSupplier(initialPublicSupplier || null);
      setStep(
        initialPublicSupplier
          ? CreateOrgSupplierV2DialogStep.EDIT_SUPPLIER
          : CreateOrgSupplierV2DialogStep.SEARCH
      );
    }
  }, [dialogOpen, methods.reset, defaultValues, initialPublicSupplier]);

  const handleSetSelectedSupplier = (
    brand: BrandfetchSearchResult | null,
    publicSupplier: PublicSupplier | null
  ) => {
    setSelectedBrand(brand);
    setSelectedPublicSupplier(publicSupplier);
    setStep(CreateOrgSupplierV2DialogStep.EDIT_SUPPLIER);
    methods.setValue(formConstants.name.id, brand?.name || '');
    methods.setValue(formConstants.website.id, brand?.domain || '');
  };

  return (
    <BaseCreateRecordDialog
      open={dialogOpen}
      onClose={() => {
        onClose();
        setSelectedBrand(null);
        setSearchString('');
        setSelectedPublicSupplier(null);
        setStep(CreateOrgSupplierV2DialogStep.SEARCH);
      }}
      dialogProps={dialogProps}
      maxWidth="md"
    >
      <Stack
        direction="column"
        spacing={gridSpacing}
        sx={{
          justifyContent: 'flex-start',
          alignItems: 'stretch',
        }}
      >
        {/* Search Bar */}
        <Grid
          container
          spacing={actionButtonSpacing}
          sx={{
            alignItems: 'center',
          }}
        >
          {(step === CreateOrgSupplierV2DialogStep.SEARCH ||
            !!selectedBrand) && (
            <Grid size="grow">
              <TextField
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {searchStringIsLink ? (
                        <IconWorldSearch stroke={1.5} fontSize="small" />
                      ) : (
                        <IconSearch stroke={1.5} fontSize="small" />
                      )}
                    </InputAdornment>
                  ),
                  endAdornment: isBrandfetchSearchLoading ? (
                    <CircularProgress size={20} />
                  ) : undefined,
                }}
                onChange={(e) => setSearchString(e.target.value)}
                placeholder="Search for a supplier name, or paste a link to a supplier website"
                value={searchString}
                size="small"
                disabled={step !== CreateOrgSupplierV2DialogStep.SEARCH}
              />
            </Grid>
          )}
          <Grid size="auto">
            {step !== CreateOrgSupplierV2DialogStep.SEARCH ? (
              <SegmentButton
                variant="outlined"
                color="primary"
                onClick={() => {
                  handleSetSelectedSupplier(null, null);
                  setStep(CreateOrgSupplierV2DialogStep.SEARCH);
                }}
                startIcon={<IconArrowLeft />}
              >
                Back
              </SegmentButton>
            ) : (
              <SegmentButton
                variant="outlined"
                color="primary"
                onClick={() =>
                  setStep(CreateOrgSupplierV2DialogStep.EDIT_SUPPLIER)
                }
                startIcon={<IconPencil />}
              >
                Enter Info Manually
              </SegmentButton>
            )}
          </Grid>
        </Grid>
        {/* Brandfetch Search Results */}
        {step === CreateOrgSupplierV2DialogStep.SEARCH &&
          brandfetchSearchResults &&
          brandfetchSearchResults.length > 0 &&
          debouncedSearchString.length > 0 && (
            <Grid container>
              {brandfetchSearchResults.map((result) => {
                const publicSupplier = publicSuppliers?.data?.find(
                  (supplier) =>
                    supplier.website?.toLowerCase() ===
                      result.domain?.toLowerCase() ||
                    supplier.name?.toLowerCase() ===
                      result.name?.toLowerCase() ||
                    supplier.altNames?.some(
                      (altName) =>
                        altName?.toLowerCase() === result.name?.toLowerCase()
                    )
                );
                return (
                  <Grid
                    key={result.brandId}
                    container
                    size={12}
                    justifyContent="space-between"
                    alignItems="center"
                    direction="row"
                    onClick={() => {
                      handleSetSelectedSupplier(result, publicSupplier || null);
                      setStep(CreateOrgSupplierV2DialogStep.EDIT_SUPPLIER);
                    }}
                    sx={{
                      padding: theme.spacing(1, 2),
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                      '&:last-child': {
                        borderBottom: 'none',
                      },
                    }}
                  >
                    <Grid container size={10}>
                      <Grid size={12}>
                        <Stack
                          direction="row"
                          spacing={actionButtonSpacing}
                          sx={{
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                          }}
                        >
                          {!!result.icon && (
                            <Avatar
                              src={result.icon}
                              sx={{ width: 36, height: 36 }}
                            />
                          )}
                          <Stack
                            direction="column"
                            sx={{
                              justifyContent: 'center',
                              alignItems: 'flex-start',
                            }}
                          >
                            <Typography variant="h5">{result.name}</Typography>
                            <Typography variant="caption">
                              {result.domain}
                            </Typography>
                          </Stack>
                          {isFetchingPublicSuppliers && (
                            <Skeleton variant="text" width={200} height={24} />
                          )}
                          {!isFetchingPublicSuppliers && !!publicSupplier && (
                            <Stack
                              direction="row"
                              spacing={0.5}
                              sx={{ alignItems: 'center', pl: gridSpacing }}
                            >
                              <IconCircleCheck
                                color={theme.palette.success.main}
                              />
                              <Typography variant="caption">
                                In Cofactr Supplier Network
                              </Typography>
                            </Stack>
                          )}
                        </Stack>
                      </Grid>
                    </Grid>
                    <Grid container size={2} justifyContent="flex-end">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        sx={{
                          justifyContent: 'flex-end',
                          alignItems: 'center',
                        }}
                      >
                        <SegmentButton
                          variant="contained"
                          color="primary"
                          onClick={() => {
                            handleSetSelectedSupplier(
                              result,
                              publicSupplier || null
                            );
                            setStep(
                              CreateOrgSupplierV2DialogStep.EDIT_SUPPLIER
                            );
                          }}
                          startIcon={<IconCheck />}
                        >
                          Select
                        </SegmentButton>
                        <InlineGoToButton
                          link={`https://${result.domain}`}
                          tooltip="View Website"
                          recordName="Website"
                          Icon={IconExternalLink}
                          isExternalLink
                        />
                      </Stack>
                    </Grid>
                  </Grid>
                );
              })}
            </Grid>
          )}
        {/* NoSearch Results State */}
        {step === CreateOrgSupplierV2DialogStep.SEARCH &&
          !isBrandfetchSearchLoading &&
          brandfetchSearchResults &&
          brandfetchSearchResults.length === 0 &&
          searchString.length > 0 && (
            <Typography variant="body1">No search results found</Typography>
          )}
        {/* Selected Result Section */}
        {step === CreateOrgSupplierV2DialogStep.EDIT_SUPPLIER &&
          !!selectedBrand && (
            <Grid
              container
              size={12}
              justifyContent="space-between"
              alignItems="center"
              direction="row"
              sx={{
                padding: theme.spacing(1, 2),
                borderBottom: `1px solid ${theme.palette.divider}`,
                '&:last-child': {
                  borderBottom: 'none',
                },
              }}
            >
              <Grid container size={12}>
                <Grid size={12}>
                  <Stack
                    direction="row"
                    spacing={actionButtonSpacing}
                    sx={{
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                    }}
                  >
                    {!!selectedBrand.icon && (
                      <Avatar
                        src={selectedBrand.icon}
                        sx={{ width: 36, height: 36 }}
                      />
                    )}
                    <Stack
                      direction="column"
                      sx={{
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                      }}
                    >
                      <Typography variant="h5">{selectedBrand.name}</Typography>
                      <Typography variant="caption">
                        {selectedBrand.domain}
                      </Typography>
                    </Stack>
                    {!!selectedPublicSupplier && (
                      <Stack
                        direction="row"
                        spacing={0.5}
                        sx={{ alignItems: 'center', pl: gridSpacing }}
                      >
                        <IconCircleCheck color={theme.palette.success.main} />
                        <Typography variant="caption">
                          In Cofactr Supplier Network
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </Grid>
          )}
        {/* Edit Supplier Form */}
        <Stack
          direction="column"
          sx={{
            justifyContent: 'flex-start',
            alignItems: 'stretch',
          }}
        >
          {step === CreateOrgSupplierV2DialogStep.EDIT_SUPPLIER &&
            org?.allowSelfManagedPurchasing && (
              <HookFormToggleCardButton
                key={formConstants.connectionType.id}
                name={formConstants.connectionType.id}
                control={methods.control}
                errors={methods.formState.errors}
                label={formConstants.connectionType.label}
                options={[
                  {
                    value: OrgSupplierConnection.SUPPLIER_NETWORK,
                    label:
                      OrgSupplierConnectionLabels[
                        OrgSupplierConnection.SUPPLIER_NETWORK
                      ],
                    description: `Automatically set up the connection between Cofactr & ${
                      selectedBrand?.name || 'this supplier'
                    }. We will fully manage your ${
                      selectedBrand?.name || ''
                    } purchases for you.`,
                    icon: IconTopologyStar,
                  },
                  {
                    value: OrgSupplierConnection.DIRECT,
                    label:
                      OrgSupplierConnectionLabels[OrgSupplierConnection.DIRECT],
                    description: `Configure your own connection to ${
                      selectedBrand?.name || 'this supplier'
                    }.`,
                    icon: IconRoute2,
                  },
                ]}
              />
            )}
          {step === CreateOrgSupplierV2DialogStep.EDIT_SUPPLIER &&
            !selectedBrand && (
              <>
                <HookFormInput
                  control={methods.control}
                  errors={methods.formState.errors}
                  label={formConstants.name.label}
                  name={formConstants.name.id}
                />
                <HookFormInput
                  control={methods.control}
                  errors={methods.formState.errors}
                  label={formConstants.website.label}
                  name={formConstants.website.id}
                />
              </>
            )}
          {step === CreateOrgSupplierV2DialogStep.EDIT_SUPPLIER && (
            <>
              <Typography variant="h5" sx={{ my: gridSpacing }}>
                Optional: Add Contact Information
              </Typography>
              <HookFormInput
                control={methods.control}
                errors={methods.formState.errors}
                label={formConstants.quotingEmail.label}
                name={formConstants.quotingEmail.id}
                helperText={formConstants.quotingEmail.description}
              />
            </>
          )}
          {step === CreateOrgSupplierV2DialogStep.EDIT_SUPPLIER &&
            !!selectedPublicSupplier &&
            selectedPublicSupplier.loginRequired !==
              LoginRequired.NOT_REQUIRED && (
              <>
                <Typography variant="h5" sx={{ mb: 1, mt: gridSpacing }}>
                  {selectedPublicSupplier.loginRequired ===
                  LoginRequired.REQUIRED
                    ? 'Required'
                    : 'Recommended'}
                  : Add Login Credentials
                </Typography>
                <Typography variant="caption" sx={{ mb: gridSpacing }}>
                  Add your login to your secure Cofactr vault to allow AIlana to
                  login to this supplier&apos;s website on your behalf to fetch
                  part information and pricing. We will only use this
                  information to fetch information for your use in Cofactr and
                  will not take action with this supplier on your behalf without
                  your permission.
                </Typography>
                <HookFormInput
                  name={formConstants.username.id}
                  label={formConstants.username.label}
                  control={methods.control}
                  errors={methods.formState.errors}
                  helperText={formConstants.username.description}
                />
                <HookFormInput
                  name={formConstants.password.id}
                  label={formConstants.password.label}
                  control={methods.control}
                  errors={methods.formState.errors}
                  type="password"
                  helperText={formConstants.password.description}
                />
              </>
            )}
        </Stack>
        {duplicateSuppliers.length > 0 && !!selectedPublicSupplier && (
          <Alert severity="error">
            <AlertTitle>Duplicate Supplier</AlertTitle>
            You already have a Supplier record for{' '}
            {selectedPublicSupplier?.name}. To edit settings for{' '}
            {selectedPublicSupplier?.name}, search for{' '}
            {selectedPublicSupplier?.name} in the Supplier view.
          </Alert>
        )}
      </Stack>
    </BaseCreateRecordDialog>
  );
};

export default CreateOrgSupplierV2Dialog;
